export type PasswordRuleId =
  | 'minLength'
  | 'uppercase'
  | 'lowercase'
  | 'number'
  | 'specialChar'

export interface PasswordRule {
  id: PasswordRuleId
  label: string
  satisfied: boolean
}

export interface PasswordRequirementsState {
  rules: PasswordRule[]
  allMet: boolean
}

const SPECIAL_CHAR_REGEX = /[^A-Za-z0-9]/

const RULE_CHECKS: Record<PasswordRuleId, (password: string) => boolean> = {
  minLength: (password) => password.length >= 12,
  uppercase: (password) => /[A-Z]/.test(password),
  lowercase: (password) => /[a-z]/.test(password),
  number: (password) => /[0-9]/.test(password),
  specialChar: (password) => SPECIAL_CHAR_REGEX.test(password),
}

// Rule order determines display order in the checklist
const RULE_IDS: PasswordRuleId[] = [
  'minLength',
  'uppercase',
  'lowercase',
  'number',
  'specialChar',
]

/**
 * Evaluates a password against the project Cognito password policy rules.
 * Labels are provided by the caller (i18n-resolved) and passed through as-is.
 */
export function checkPasswordRequirements(
  password: string,
  labels: Record<PasswordRuleId, string>
): PasswordRequirementsState {
  const rules: PasswordRule[] = RULE_IDS.map((id) => ({
    id,
    label: labels[id],
    satisfied: RULE_CHECKS[id](password),
  }))

  return {
    rules,
    allMet: rules.every((rule) => rule.satisfied),
  }
}
